import { useAtom, useCtx } from '@reatom/npm-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Array, Iterable, pipe } from '@blade/std-lib'
import { Tooltip } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import {
  reatomAggregatedPosition,
  type AggregatedPositionModel,
  type WalletPositionModel,
} from '@root/entities/position'
import { walletsByIdAtom } from '@root/entities/wallet'
import { WalletAddressTruncated } from '@root/entities/wallet/ui/common/wallet-address-truncated'
import { WalletIcon } from '@root/shared/ui-kit'

import type { WalletModel } from '@root/entities/wallet'

type Props = {
  position: WalletPositionModel | AggregatedPositionModel
  onWalletsClick?: (value: AggregatedPositionModel, e: React.MouseEvent) => void
}

export const PositionsTableWalletCell: React.FC<Props> = ({
  position,
  onWalletsClick,
}) => {
  const { t } = useTranslation()
  const ctx = useCtx()

  const [wallets] = useAtom(
    (ctx) => {
      const walletsById = ctx.spy(walletsByIdAtom)
      if (reatomAggregatedPosition.is(position)) {
        const walletPositions = ctx.spy(position.walletPositions)
        return pipe(
          walletPositions,
          Iterable.map((walletPosition) =>
            walletsById.get(walletPosition.walletId),
          ),
          Iterable.filter((it) => it !== undefined),
          Iterable.toArray,
        )
      }

      const wallet = walletsById.get(position.walletId)
      if (!wallet) {
        return []
      }

      return [wallet]
    },
    [position],
  )

  const handleClick: React.MouseEventHandler = (e) => {
    if (wallets.length > 1 && reatomAggregatedPosition.is(position)) {
      onWalletsClick?.(position, e)
      return
    }
  }

  if (wallets.length === 0) {
    return <>-</>
  }

  const renderContent = () => {
    if (wallets.length === 1) {
      const wallet = wallets[0]!
      return <SingleWallet wallet={wallet} />
    }
    const text = `${wallets.length} ${t('wallet', { count: wallets.length })}`
    return (
      <>
        <WalletIcon size={14} />
        <WalletsText>{text}</WalletsText>
      </>
    )
  }

  const renderTooltipContent = () => {
    if (reatomAggregatedPosition.is(position)) {
      return <AggregatedTooltipContent position={position} />
    }

    const wallet = wallets.at(0)
    return wallet ? ctx.get(wallet.name) : undefined
  }

  const clickable = wallets.length > 1 && !!onWalletsClick

  return (
    <Tooltip content={renderTooltipContent()}>
      <Root $clickable={clickable} onClick={handleClick}>
        {renderContent()}
      </Root>
    </Tooltip>
  )
}

const Root = styled.div<{ $clickable: boolean }>`
  display: grid;
  align-items: center;
  grid-auto-flow: column;

  padding-block: 2px;
  padding-inline: 4px 8px;
  gap: 6px;
  height: 20px;

  border-radius: 12px;
  background: var(--background-3);
  ${({ $clickable }) =>
    $clickable
      ? css`
          cursor: pointer;
          &:hover {
            background: var(--background-4);
          }
        `
      : undefined};

  color: var(--text-11);
  text-align: center;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`

const WalletsText = styled.div`
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SingleWallet: React.FC<{ wallet: WalletModel }> = ({ wallet }) => {
  const [color] = useAtom(wallet.color)

  return (
    <>
      <WalletIcon color={color} size={14} />
      <WalletAddressTruncated walletAddress={wallet.address} />
    </>
  )
}

const AggregatedTooltipContent: React.FC<{
  position: AggregatedPositionModel
}> = ({ position }) => {
  const ctx = useCtx()
  const [walletPositions] = useAtom(position.walletPositions)

  const singleWalletPosition = Array.single(walletPositions)
  if (singleWalletPosition) {
    const wallet = ctx.get(singleWalletPosition.walletAtom)
    return wallet ? ctx.get(wallet.name) : undefined
  }

  return walletPositions.map((it, idx, arr) => (
    <>
      <WalletAddressTruncated walletAddress={it.walletAddress} />{' '}
      <span>
        (
        <Numeric
          value={ctx.get(it.remainingSizeCurrency)}
          currency={it.pair.quote.ticker}
          preset="currency-amount-short"
        />
        )
      </span>
      {idx < arr.length - 1 && <br />}
    </>
  ))
}
